import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { animate, AnimationEvent, style, transition, trigger } from '@angular/animations';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AvanoComboboxTemplateDirective, ComboboxEntry } from './avano-combobox.common';

export const AVANO_COMBOBOX_SINGLE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AvanoComboboxSingleComponent),
  multi: true
};

@Component({
  selector: 'avano-combobox-single',
  templateUrl: './avano-combobox-single.component.html',
  styleUrls: ['./avano-combobox-single.component.scss'],
  providers: [AVANO_COMBOBOX_SINGLE_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('overlayAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'scaleY(0.8)' }), animate('.12s cubic-bezier(0, 0, 0.2, 1)')]),
      transition(':leave', [animate('.1s linear', style({ opacity: 0 }))])
    ])
  ]
})
export class AvanoComboboxSingleComponent implements AfterContentInit, ControlValueAccessor {
  // Indicator if filtered value is not found identically in the list of choices a new entry can be created
  @Input() enableNewEntry = false;
  @Input() hideOnSelection = true;
  @Input() enableFilter = true;

  // The entries the user can choose from are coming from the outside
  @Input() selectableEntries: ComboboxEntry[] = [];
  // But as we perhaps allow filtering, we make later a copy of the selectable entries
  selectableEntriesFiltered: ComboboxEntry[] = [];

  @Output() newEntry = new EventEmitter<string>();

  @Input() selectedEntry: any;
  @Input() initialField: string;
  @Output() selectedEntryChange = new EventEmitter<any>();

  @Input() disabled: boolean;

  @ViewChild('container') containerViewChild: ElementRef;
  @ViewChild('filterInput') filterInputChild: ElementRef;
  @ContentChildren(AvanoComboboxTemplateDirective) templates: QueryList<any>;

  @Input() labelNew: any = 'Create new';
  @Input() labelFilterCreate: any = 'Filter/Create new';
  @Input() labelFilter: any = 'Filter';

  public overlayVisible = false;

  private documentClickListener: any;

  private overlay: HTMLDivElement;

  public selectedItemTemplate: TemplateRef<any>;

  public onModelChange: Function = () => {};

  public onModelTouched: Function = () => {};

  constructor(public el: ElementRef, public renderer: Renderer2, public cd: ChangeDetectorRef) {}

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'selectedItem':
          this.selectedItemTemplate = item.template;
          break;
        default:
          break;
      }
    });
  }

  public overlayToggle(event: MouseEvent): void {
    // We only want to toggle if the click happened on the element, not on the overlay
    if (!this.disabled && !this.isOverlayClick(event)) {
      if (this.overlayVisible) {
        this.overlayHide();
      } else {
        this.overlayShow();
      }
    }
  }

  public overlayShow(): void {
    this.selectableEntriesFiltered = [...this.selectableEntries];
    this.overlayVisible = true;
    this.documentClickListenerBind();
  }

  public overlayHide(): void {
    this.overlayVisible = false;
    this.documentClickListenerUnbind();
    this.cd.markForCheck();
  }

  private documentClickListenerBind() {
    if (!this.documentClickListener) {
      const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

      this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
        if (this.isOutsideClicked(event)) {
          this.overlayHide();
        }
      });
    }
  }

  private isOverlayClick(event: MouseEvent): boolean {
    const targetNode = <Node>event.target;
    return this.overlay ? this.overlay.isSameNode(targetNode) || this.overlay.contains(targetNode) : false;
  }

  private isOutsideClicked(event: MouseEvent): boolean {
    return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target) || this.isOverlayClick(event));
  }

  private documentClickListenerUnbind() {
    if (this.documentClickListener) {
      this.documentClickListener(); // Calling the listener is unlistening
      this.documentClickListener = null;
    }
  }

  public clear(event: MouseEvent): void {
    this.selectedEntry = undefined;
    this.onModelChange(this.selectedEntry);
    event.preventDefault();
    event.stopPropagation();
  }

  public createNewEntry(usersCurrentInput: string): void {
    this.selectedEntry = {
      label: usersCurrentInput,
      value: undefined
    };
    this.newEntry.emit(usersCurrentInput);
    if (this.hideOnSelection) {
      this.overlayHide();
    }
  }

  public selectEntry(clickedEntry: ComboboxEntry): void {
    this.selectedEntry = clickedEntry;
    this.selectedEntryChange.emit(this.selectedEntry.value);
    if (this.hideOnSelection) {
      this.overlayHide();
    }
    this.onModelChange(this.selectedEntry.value);
  }

  getOptionLabel(option: ComboboxEntry) {
    if (option?.label) {
      return option.label;
    } else if (option?.value) {
      return option.value;
    } else {
      return option; // Used when its a new entry
    }
  }

  isEntrySelected(option: ComboboxEntry): boolean {
    if (this.selectedEntry?.label) {
      return option.label === this.selectedEntry.label;
    } else {
      return option.label === this.selectedEntry;
    }
  }

  onFilterInputChange(currentFilterValue: string) {
    this.selectableEntriesFiltered = this.selectableEntries
      .filter((singleEntry: any) => {
        return singleEntry.label.toLowerCase().startsWith(currentFilterValue.trim().toLowerCase());
      })
      .slice();
  }

  onOverlayAnimationStart(event: AnimationEvent) {
    if (event.toState === 'visible') {
      this.overlay = event.element;
      if (!this.overlay.style.minWidth) {
        // Lets be as wide as our parent (the input/dropdown)
        this.overlay.style.minWidth = this.getWidth(this.containerViewChild.nativeElement) + 'px';
      }

      if (this.enableFilter && this.filterInputChild && this.filterInputChild.nativeElement) {
        this.filterInputChild.nativeElement.focus();
      }
    }
  }

  private getWidth(el): number {
    const computedStyle = getComputedStyle(el);
    const width =
      el.offsetWidth -
      parseFloat(computedStyle.paddingLeft) -
      parseFloat(computedStyle.paddingRight) -
      parseFloat(computedStyle.borderLeftWidth) -
      parseFloat(computedStyle.borderRightWidth);

    return width;
  }

  isNotSelectable(currentUserInput: string): boolean {
    if (!currentUserInput || !currentUserInput.length || !currentUserInput.trim().length) {
      return false;
    } else {
      return !this.selectableEntries.find((singleEntry: ComboboxEntry) => {
        if (singleEntry.label) {
          return singleEntry.label.toLowerCase() === currentUserInput.trim().toLowerCase();
        } else {
          return singleEntry.value.toLowerCase() === currentUserInput.trim().toLowerCase();
        }
      });
    }
  }

  // To work with the form stuff from angular we need to implement a dedicated interface
  // BEGIN: Methods for ControlValueAccessor interface
  writeValue(value: any): void {
    if (this.initialField && value && value[this.initialField]) {
      this.selectedEntry = value[this.initialField];
    } else {
      this.selectedEntry = value;
    }
    this.cd.markForCheck();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
    this.cd.markForCheck();
  }
  // END: Methods for ControlValueAccessor interface
}
